import React, { useState } from 'react';
import { FaQrcode, FaWhatsapp, FaChartLine, FaCloud, FaCheck, FaStar, FaEnvelope, FaPhone, FaMapMarkerAlt, FaArrowRight, FaUtensils, FaMobile, FaUsers, FaShieldAlt, FaTags } from 'react-icons/fa';
import EatEzyLogo from '../components/Common/EatEzyLogo';
import LandingFooter from "../components/Common/LandingFooter";

const EatEzyHome = () => {
  const [formData, setFormData] = useState({
    name: '',
    businessName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name.trim() || !formData.businessName.trim() || !formData.phone.trim()) {
      alert('Please fill in all required fields (Name, Business Name, and Phone Number).');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL || "https://eateazyapi.yorotechnologies.com/api"}/new/enquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setFormData({
          name: '',
          businessName: '',
          email: '',
          phone: '',
          message: ''
        });
        setShowSuccess(true);
      } else {
        throw new Error('Failed to submit enquiry');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Sorry, there was an error submitting your enquiry. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    {
      icon: <FaQrcode className="w-8 h-8" />,
      title: "Digital Menu",
      description: "Create stunning digital menus with QR code access. Easy to update, mobile-responsive, and always up-to-date for your customers."
    },
    {
      icon: <FaWhatsapp className="w-8 h-8" />,
      title: "WhatsApp Ordering",
      description: "Seamless WhatsApp integration for direct customer ordering. Customers can browse, order, and pay through WhatsApp messaging."
    },
    {
      icon: <FaUsers className="w-8 h-8" />,
      title: "Products & Categories Management",
      description: "Complete management of products and categories — organize menus, update items fast, and keep everything consistent."
    },
    {
      icon: <FaTags className="w-8 h-8" />,
      title: "Offer Management",
      description: "Create and manage special offers, discounts, and promotions. Track performance and boost sales with targeted campaigns."
    },
    {
      icon: <FaCloud className="w-8 h-8" />,
      title: "Cloud-Based SaaS",
      description: "Fully cloud-based solution with automatic updates, 99.9% uptime, and scalable infrastructure for growing businesses."
    },
    {
      icon: <FaStar className="w-8 h-8" />,
      title: "Themes & Banner Backgrounds",
      description: "Choose from multiple themes and ready-made banner background images to match your brand and season."
    },
    {
      icon: <FaMobile className="w-8 h-8" />,
      title: "Reels & Social Links",
      description: "Add reels / short-video links from social media (Instagram, YouTube, etc.) to boost engagement and conversions."
    },
    {
      icon: <FaUtensils className="w-8 h-8" />,
      title: "Perfect for Any Food Industry",
      description: "Restaurant, cafe, foodcourt, cloud kitchen, desserts & bakes, pastries, coolbar, and cakes."
    }
  ];

  const pricingPlans = [
    {
      name: "Starter Plan",
      price: "$29",
      period: "/month",
      description: "Perfect for small restaurants and cafes",
      features: [
        "Up to 50 menu items",
        "QR code generation",
        "WhatsApp ordering",
        "Basic analytics",
        "Email support",
        "Mobile responsive"
      ],
      popular: false
    },
    {
      name: "Growth Plan",
      price: "$79",
      period: "/month",
      description: "Ideal for growing businesses",
      features: [
        "Up to 200 menu items",
        "Advanced analytics",
        "Multi-location support",
        "Custom branding",
        "Priority support",
        "API access",
        "Inventory management"
      ],
      popular: true
    },
    {
      name: "Enterprise Plan",
      price: "Custom",
      period: "",
      description: "For chains and franchises",
      features: [
        "Unlimited menu items",
        "White-label solution",
        "Custom integrations",
        "Dedicated account manager",
        "24/7 phone support",
        "Advanced reporting",
        "Multi-currency support"
      ],
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Custom CSS Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes fadeInUp {
          0% { 
            opacity: 0; 
            transform: translateY(30px); 
          }
          100% { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        
        @keyframes slideInLeft {
          0% { 
            opacity: 0; 
            transform: translateX(-50px); 
          }
          100% { 
            opacity: 1; 
            transform: translateX(0); 
          }
        }
        
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        @keyframes pulse-slow {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
        
        @keyframes bounce-x {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(5px); }
        }
        
        @keyframes slideInRight {
          0% { 
            opacity: 0; 
            transform: translateX(50px); 
          }
          100% { 
            opacity: 1; 
            transform: translateX(0); 
          }
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.8s ease-out forwards;
          opacity: 0;
        }
        
        .animate-slideInLeft {
          animation: slideInLeft 1s ease-out forwards;
          opacity: 0;
        }
        
        .animate-slideInRight {
          animation: slideInRight 1s ease-out forwards;
          opacity: 0;
        }
        
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 2s ease-in-out infinite;
        }
        
        .animate-bounce-x {
          animation: bounce-x 1s ease-in-out infinite;
        }
      `}</style>
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 gap-4">
            <div className="flex-shrink-0">
              <EatEzyLogo size="default" showTagline={true} />
            </div>
            <div className="hidden md:flex items-center space-x-8 flex-shrink-0">
              <a href="#features" className="text-gray-600 hover:text-teal-600 transition-colors">Features</a>
              <a href="#contact" className="text-gray-600 hover:text-teal-600 transition-colors">Contact</a>
            </div>
            <div className="flex-shrink-0">
              <a
                href="https://eatezyadmin.yorokart.com/"
                className="inline-flex items-center justify-center min-w-[100px] px-6 py-3 rounded-xl font-bold text-base whitespace-nowrap shadow-lg border-2 border-teal-600 bg-white hover:bg-teal-50 transition-all duration-200 no-underline"
                style={{ color: '#0f766e' }}
              >
                Login
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-50 via-white to-teal-50/30 py-20 lg:py-32 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1555396273-367ea4eb4db5?auto=format&fit=crop&w=2074&q=80')"
          }}
        ></div>
        
        {/* Animated Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-r from-teal-50/30 to-orange-50/30"></div>
        
        {/* Floating Food Icons Animation */}
        <div className="absolute top-20 left-10 animate-bounce" style={{animationDelay: '0s', animationDuration: '3s'}}>
          <div className="text-4xl opacity-20">🍕</div>
        </div>
        <div className="absolute top-40 right-20 animate-bounce" style={{animationDelay: '1s', animationDuration: '4s'}}>
          <div className="text-3xl opacity-20">🍔</div>
        </div>
        <div className="absolute bottom-40 left-20 animate-bounce" style={{animationDelay: '2s', animationDuration: '3.5s'}}>
          <div className="text-3xl opacity-20">🍜</div>
        </div>
        <div className="absolute top-60 left-1/3 animate-bounce" style={{animationDelay: '0.5s', animationDuration: '4.5s'}}>
          <div className="text-2xl opacity-20">🍣</div>
        </div>
        <div className="absolute bottom-60 right-1/3 animate-bounce" style={{animationDelay: '1.5s', animationDuration: '3.8s'}}>
          <div className="text-3xl opacity-20">🌮</div>
        </div>
        
        {/* Animated Gradient Orbs */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-teal-100/20 to-orange-100/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-orange-100/20 to-teal-100/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        
        {/* Floating Tech Icons */}
        <div className="absolute top-32 right-1/4 animate-float" style={{animationDelay: '0s'}}>
          <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
            <FaQrcode className="text-teal-600 text-xl" />
          </div>
        </div>
        <div className="absolute bottom-32 left-1/4 animate-float" style={{animationDelay: '2s'}}>
          <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
            <FaWhatsapp className="text-green-500 text-xl" />
          </div>
        </div>
        <div className="absolute top-1/2 right-10 animate-float" style={{animationDelay: '1s'}}>
          <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
            <FaCloud className="text-orange-500 text-xl" />
          </div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative z-10 animate-fadeInUp">
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight animate-slideInLeft">
                The Future of
                <span className="block text-red bg-clip-text bg-gradient-to-r from-teal-600 to-orange-500 animate-gradient" style={{color : "red"}}>
                  Digital Menus
                </span>
                & WhatsApp Ordering
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed animate-fadeInUp" style={{animationDelay: '0.2s'}}>
                Transform your restaurant with our cloud-based SaaS platform. 
                Create stunning digital menus, enable WhatsApp ordering, and manage 
                multiple locations from one powerful dashboard.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 animate-fadeInUp" style={{animationDelay: '0.4s'}}>
                
                <a href="#contact" className="inline-block border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-xl font-semibold text-lg hover:border-teal-500 hover:text-teal-600 transition-all duration-300 hover:scale-105">
                  Book a Demo
                </a>
              </div>
              <div className="mt-8 flex items-center space-x-6 text-sm text-gray-500">
                <div className="flex items-center">
                  <FaCheck className="text-green-500 mr-2" />
                  No setup fees
                </div>
                <div className="flex items-center">
                  <FaCheck className="text-green-500 mr-2" />
                  14-day free trial
                </div>
                <div className="flex items-center">
                  <FaCheck className="text-green-500 mr-2" />
                  Cancel anytime
                </div>
              </div>
            </div>
            <div className="relative z-10 animate-slideInRight" style={{animationDelay: '0.6s'}}>
              <div className="relative bg-gradient-to-br from-teal-500/10 via-white to-orange-500/10 rounded-3xl p-1 shadow-xl border border-teal-200/50 hover:shadow-2xl hover:border-teal-300/60 transition-all duration-500 group">
                <div className="bg-white rounded-2xl p-6 shadow-inner overflow-hidden">
                  {/* Header - browser-style */}
                  <div className="flex items-center justify-between mb-5 pb-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center shadow-sm">
                        <FaUtensils className="w-5 h-5 text-teal-600" />
                      </div>
                      <div>
                        <span className="font-bold text-gray-900 block leading-tight">Restaurant Dashboard</span>
                        <span className="text-xs text-gray-500">Manage everything in one place</span>
                      </div>
                    </div>
                    <div className="flex gap-1.5">
                      <div className="w-2.5 h-2.5 bg-red-400 rounded-full group-hover:bg-red-500 transition-colors"></div>
                      <div className="w-2.5 h-2.5 bg-amber-400 rounded-full group-hover:bg-amber-500 transition-colors"></div>
                      <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full group-hover:bg-emerald-600 transition-colors"></div>
                    </div>
                  </div>
                  {/* Quick stats */}
                  <div className="grid grid-cols-3 gap-3 mb-5">
                    <div className="rounded-xl p-3 border border-teal-100 bg-gradient-to-br from-teal-50 to-white">
                      <div className="text-[11px] text-gray-500 mb-1">Today’s Orders</div>
                      <div className="text-lg font-extrabold text-gray-900 leading-none">128</div>
                      <div className="text-[11px] text-emerald-600 mt-1">+12%</div>
                    </div>
                    <div className="rounded-xl p-3 border border-orange-100 bg-gradient-to-br from-orange-50 to-white">
                      <div className="text-[11px] text-gray-500 mb-1">Banners</div>
                      <div className="text-lg font-extrabold text-gray-900 leading-none">12</div>
                      <div className="text-[11px] text-emerald-600 mt-1">Scheduled</div>
                    </div>
                    <div className="rounded-xl p-3 border border-gray-100 bg-gradient-to-br from-gray-50 to-white">
                      <div className="text-[11px] text-gray-500 mb-1">Reels</div>
                      <div className="text-lg font-extrabold text-gray-900 leading-none">24</div>
                      <div className="text-[11px] text-teal-700 mt-1">Published</div>
                    </div>
                  </div>

                  {/* Quick actions */}
                  <div className="grid grid-cols-2 gap-3 mb-5">
                    <div className="rounded-xl border border-teal-100 bg-white p-4 hover:border-teal-200 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center border border-teal-100">
                            <FaUtensils className="w-4 h-4 text-teal-700" />
                          </div>
                          <div>
                            <div className="text-sm font-bold text-gray-900 leading-tight">Add Product</div>
                            <div className="text-[11px] text-gray-500">Menu item, variants</div>
                          </div>
                        </div>
                        <FaArrowRight className="w-4 h-4 text-teal-600" />
                      </div>
                    </div>
                    <div className="rounded-xl border border-orange-100 bg-white p-4 hover:border-orange-200 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center border border-orange-100">
                            <FaTags className="w-4 h-4 text-orange-700" />
                          </div>
                          <div>
                            <div className="text-sm font-bold text-gray-900 leading-tight">Create Offer</div>
                            <div className="text-[11px] text-gray-500">Discounts & combos</div>
                          </div>
                        </div>
                        <FaArrowRight className="w-4 h-4 text-orange-600" />
                      </div>
                    </div>
                    <div className="rounded-xl border border-gray-100 bg-white p-4 hover:border-gray-200 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center border border-gray-100">
                            <FaChartLine className="w-4 h-4 text-gray-700" />
                          </div>
                          <div>
                            <div className="text-sm font-bold text-gray-900 leading-tight">Analytics</div>
                            <div className="text-[11px] text-gray-500">Sales & trends</div>
                          </div>
                        </div>
                        <FaArrowRight className="w-4 h-4 text-gray-700" />
                      </div>
                    </div>
                    <div className="rounded-xl border border-green-100 bg-white p-4 hover:border-green-200 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center border border-green-100">
                            <FaWhatsapp className="w-4 h-4 text-green-700" />
                          </div>
                          <div>
                            <div className="text-sm font-bold text-gray-900 leading-tight">Orders</div>
                            <div className="text-[11px] text-gray-500">WhatsApp flow</div>
                          </div>
                        </div>
                        <FaArrowRight className="w-4 h-4 text-green-700" />
                      </div>
                    </div>
                  </div>

                  {/* Recent activity */}
                  <div className="rounded-xl border border-gray-100 bg-gradient-to-br from-white to-gray-50 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-bold text-gray-900">Recent Activity</span>
                      <span className="text-xs text-gray-500">Live</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-700">New order received</span>
                        <span className="text-gray-500">2m</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-700">Offer “Weekend Combo” enabled</span>
                        <span className="text-gray-500">12m</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-700">Product “Paneer Burger” updated</span>
                        <span className="text-gray-500">1h</span>
                      </div>
                    </div>
                  </div>
                  {/* Footer badges */}
                  <div className="mt-5 pt-4 flex items-center justify-between border-t border-gray-100">
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-teal-50 rounded-lg">
                      <FaMobile className="text-teal-600 w-4 h-4" />
                      <span className="text-xs font-medium text-teal-700">Mobile Ready</span>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-lg">
                      <FaWhatsapp className="text-green-600 w-4 h-4" />
                      <span className="text-xs font-medium text-green-700">WhatsApp Orders</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gradient-to-b from-white to-gray-50/50 relative overflow-hidden">
        {/* Background Pattern */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: "url('data:image/svg+xml,%3Csvg width=\"60\" height=\"60\" viewBox=\"0 0 60 60\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cg fill=\"none\" fill-rule=\"evenodd\"%3E%3Cg fill=\"%2314B8A6\" fill-opacity=\"0.1\"%3E%3Ccircle cx=\"30\" cy=\"30\" r=\"4\"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')"
          }}
        ></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fadeInUp">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Digitize Your Restaurant
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our comprehensive SaaS platform provides digital menus, WhatsApp ordering, 
              offer management, and cloud-based solutions to modernize your restaurant operations.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center p-8 rounded-2xl bg-white/80 backdrop-blur-sm border border-gray-100 hover:shadow-xl hover:border-teal-200 transition-all duration-300 transform hover:-translate-y-1 animate-fadeInUp" style={{animationDelay: `${0.1 * index}s`}}>
                <div className="w-16 h-16 bg-gradient-to-br from-teal-100 to-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-6 text-teal-600 shadow-lg">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      {/* <section id="pricing" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fadeInUp">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the perfect plan for your restaurant. All plans include our core features 
              with no hidden fees or long-term contracts.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <div key={index} className={`relative bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-100 p-8 ${plan.popular ? 'ring-2 ring-teal-500 transform scale-105 shadow-2xl' : 'hover:shadow-xl hover:border-teal-200'} transition-all duration-300 animate-fadeInUp`} style={{animationDelay: `${0.2 * index}s`}}>
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-teal-500 to-orange-500 text-white px-4 py-2 rounded-full text-sm font-semibold flex items-center">
                      <FaStar className="mr-1" />
                      Most Popular
                    </span>
                  </div>
                )}
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-gray-600 mb-4">{plan.description}</p>
                  <div className="flex items-baseline justify-center">
                    <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                    <span className="text-gray-600 ml-1">{plan.period}</span>
                  </div>
                </div>
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <FaCheck className="text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 ${
                  plan.popular 
                    ? 'bg-gradient-to-r from-teal-500 to-orange-500 text-white hover:from-teal-600 hover:to-orange-600' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}>
                  Request Demo
                </button>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Contact Form Section */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Ready to Transform Your Restaurant?
            </h2>
            <p className="text-xl text-gray-600">
              Get in touch with our team to learn more about how EatEzy can help your business grow.
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-8">
            {showSuccess ? (
              <div className="text-center py-8 px-4 animate-fadeInUp">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-teal-500 text-white shadow-lg mb-6">
                  <FaCheck className="w-10 h-10" />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                  Thank you for your enquiry!
                </h3>
                <p className="text-lg text-gray-600 max-w-md mx-auto mb-8">
                  We have received your message and will get back to you soon. Our team typically responds within 24–48 hours.
                </p>
                <button
                  type="button"
                  onClick={() => setShowSuccess(false)}
                  className="inline-flex items-center px-6 py-3 rounded-xl font-semibold text-gray-700 bg-white border-2 border-gray-300 hover:bg-gray-100 hover:border-gray-400 transition-all duration-300"
                >
                  Submit another enquiry
                </button>
              </div>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Enter your full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Name *
                  </label>
                  <input
                    type="text"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Enter your business name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Enter your email address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Enter your phone number"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Tell us about your restaurant and how we can help..."
                />
              </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={!isSubmitting ? { color: '#0f766e' } : undefined}
                  className={`w-full py-4 rounded-xl font-semibold text-lg transition-all duration-300 transform border-2 outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 ${
                    isSubmitting 
                      ? 'bg-gray-400 cursor-not-allowed border-gray-400' 
                      : 'bg-gradient-to-r from-teal-500 to-orange-500 hover:from-teal-600 hover:to-orange-600 font-bold hover:scale-[1.02] shadow-md border-teal-600/60'
                  }`}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </div>
                  ) : (
                    'Submit Enquiry'
                  )}
                </button>
            </form>
            )}
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default EatEzyHome;
